import { useMemo } from 'react'

import Link from 'next/link'

import { websitePaths } from 'domains/website/website.paths'

import Box from 'components/atomic/atoms/box'
import Button from 'components/atomic/atoms/button'
import Text from 'components/atomic/atoms/text'
import LayoutWebsite from 'components/pages/@layouts/layout-website'

type Props = {
  error: 404 | 500
  withLayout?: boolean
}

const SharedErrorPage = ({ error, withLayout = true }: Props) => {
  const { title, subtitle } = useMemo<{
    title: string
    subtitle: string
  }>(() => {
    switch (error) {
      case 404:
        return {
          title: 'Page Not Found',
          subtitle: "We couldn't find the page you were looking for.",
        }
      case 500:
      default:
        return {
          title: 'Something Went Wrong',
          subtitle:
            'An unexpected error seems to have occurred. Why not try refreshing your page? Or you can contact us if the problem persists.',
        }
    }
  }, [error])

  const content = (
    <Box
      flex="column"
      justify="center"
      css={{
        p: '$6',
        container: 'sm',
      }}
    >
      <Box textCenter css={{ spaceY: 6 }}>
        <Box>
          {!!error && (
            <Text
              as="p"
              color="text3"
              size={{ '@initial': 'xl', '@sm': '6xl' }}
              align="center"
            >
              {error}
            </Text>
          )}
          <Text
            as="h1"
            color="text1"
            size={{ '@initial': '3xl', '@sm': '4xl' }}
            align="center"
            weight="semiBold"
          >
            {title}
          </Text>
        </Box>
        <Text as="p" color="text2" align="center" weight="medium">
          {subtitle}
        </Text>
        <Box css={{ height: 1, borderBottom: '1px solid $text4' }} />
        <Box css={{ spaceX: 3 }}>
          <Link href={websitePaths.docs}>
            <Button variant="secondary">Docs</Button>
          </Link>
          <Link href={websitePaths.blog}>
            <Button variant="secondary">Blog</Button>
          </Link>
        </Box>
      </Box>
    </Box>
  )

  const Layout = () =>
    withLayout ? (
      <LayoutWebsite meta={{ title }}>{content}</LayoutWebsite>
    ) : (
      <>{content}</>
    )

  return <Layout />
}

export default SharedErrorPage
